export const LeadActivityEvents = {
  lead_state_changed: 'lead_state_changed',
  assignee_changed: 'assignee_changed',
  unassigned: 'unassigned',
  created: 'created',
  branch_changed: 'branch_changed',
  customer_data_changed: 'customer_data_changed',
  archived: 'archived',
  unarchived: 'unarchived',
  appointment_rescheduled: 'appointment_rescheduled',
  appointment_start_time_changed: 'appointment_start_time_changed',
  appointment_end_time_changed: 'appointment_end_time_changed',
  calendar_event_created: 'calendar_event_created',
  calendar_event_updated: 'calendar_event_updated',
  calendar_event_deleted: 'calendar_event_deleted',
  call: 'call',
  call_back: 'callback',
  callback_from_location: 'callback_from_location',
  appointment_requested: 'appointment_requested',
  appointment_booked: 'appointment_booked',
  appointment_rescheduled_by_customer: 'appointment_rescheduled_by_customer',
  appointment_cancelled_by_customer: 'appointment_cancelled_by_customer',
  appointment_cancelled: 'appointment_cancelled',
  appointment_declined: 'appointment_declined',
  appointment_assignee_changed: 'appointment_assignee_changed',
  appointment_branch_service_changed: 'appointment_branch_service_changed',
  appointment_attendance_status_changed:
    'appointment_attendance_status_changed',
  comment: 'comment',
  lead_priority_changed: 'lead_priority_changed',
  extra_field_changed: 'extra_field_changed',
  product_group_changed: 'product_group_changed',
  file_uploaded: 'file_uploaded',
  file_deleted: 'file_deleted',
  customer_file_uploaded: 'customer_file_uploaded',
  customer_file_deleted: 'customer_file_deleted',
  // CLICKUP-86cb5zayy — written by the Carat integration
  order_volume_changed: 'order_volume_changed',
  carat_planning_added: 'carat_planning_added',
  carat_planning_updated: 'carat_planning_updated',
  carat_planning_reassigned: 'carat_planning_reassigned',
  trickpark_session_added: 'trickpark_session_added',
  task_note: 'task_note',
  // B2B contacts; the values are `ContactActivityValue`, null once the contact is deleted.
  contact_added: 'contact_added',
  contact_removed: 'contact_removed',
  primary_contact_changed: 'primary_contact_changed',
} as const;

export type LeadActivityType =
  (typeof LeadActivityEvents)[keyof typeof LeadActivityEvents];

export interface EditorModel {
  id: string;
  firstname?: string;
  lastname?: string;
  fullname?: string;
  email: string;
  phone: string | null;
}

export interface AppointmentValue {
  id?: string;
  when?: string;
  branch_service?: {
    branch: any;
    name: string;
    service_type?: { name?: string };
  };
  where?: string;
}

export interface CallValue {
  from: string;
  duration: number;
  status: string;
}

export interface CommentValue {
  comment: string;
  author: EditorModel;
}

export interface TaskNoteValue {
  id: string;
  note: string;
}

export interface ContactActivityValue {
  id: string;
  fullname?: string | null;
  email?: string | null;
  phone?: string | null;
  role?: string | null;
}

export interface ExtraFieldValue {
  label: string;
  value: string;
}

/**
 * Snapshot of one service-request field before/after a change
 * (value_types === 'serviceRequestUpdate'). `value` depends on the property:
 * assignee → user summary, branch_service → {id, name},
 * attendance_status → 'show' | 'no_show', utc_start_time / utc_end_time → UTC datetime string.
 */
export interface ServiceRequestUpdateValue {
  property:
    | 'assignee'
    | 'branch_service'
    | 'attendance_status'
    | 'utc_start_time'
    | 'utc_end_time';
  value: any;
  service_request: {
    id: string;
    title?: string;
    where?: string;
    when?: string;
    utc_start_time?: string;
    utc_end_time?: string;
  } | null;
}

export interface LeadActivityModel {
  id?: string;
  editor: EditorModel | null;
  activity_type: LeadActivityType;
  value_types: string | null;
  prev_value: any;
  new_value: any;
  created: string;
}

export const EVENT_TO_I18N_ID: Partial<Record<LeadActivityType, string>> = {
  [LeadActivityEvents.lead_state_changed]:
    'pages.lead.activities.changedStatus',
  [LeadActivityEvents.assignee_changed]: 'pages.lead.activities.assigned',
  [LeadActivityEvents.unassigned]: 'pages.lead.activities.unassigned',
  [LeadActivityEvents.created]: 'pages.lead.activities.created',
  [LeadActivityEvents.branch_changed]: 'pages.lead.activities.changedBranch',
  [LeadActivityEvents.customer_data_changed]:
    'pages.lead.activities.customerChanged',
  [LeadActivityEvents.lead_priority_changed]:
    'pages.lead.activities.priorityChanged',
  [LeadActivityEvents.archived]: 'pages.lead.activities.archived',
  [LeadActivityEvents.unarchived]: 'pages.lead.activities.unarchived',
  [LeadActivityEvents.appointment_rescheduled]:
    'pages.lead.activities.rescheduledAppointment',
  [LeadActivityEvents.appointment_start_time_changed]:
    'pages.lead.activities.appointmentStartTimeChanged',
  [LeadActivityEvents.appointment_end_time_changed]:
    'pages.lead.activities.appointmentEndTimeChanged',
  [LeadActivityEvents.call]: 'pages.lead.activities.call',
  [LeadActivityEvents.calendar_event_created]:
    'pages.lead.calendarEvent.created',
  [LeadActivityEvents.calendar_event_updated]:
    'pages.lead.calendarEvent.updated',
  [LeadActivityEvents.calendar_event_deleted]:
    'pages.lead.calendarEvent.deleted',
  [LeadActivityEvents.call_back]: 'pages.lead.activities.callback',
  [LeadActivityEvents.callback_from_location]:
    'pages.lead.activities.callbackFromLocation',
  [LeadActivityEvents.appointment_requested]:
    'pages.lead.activities.appointmentRequested',
  [LeadActivityEvents.appointment_booked]:
    'pages.lead.activities.appointmentBooked',
  [LeadActivityEvents.appointment_rescheduled_by_customer]:
    'pages.lead.activities.appointmentRescheduledByCustomer',
  [LeadActivityEvents.appointment_cancelled_by_customer]:
    'pages.lead.activities.appointmentCancelledByCustomer',
  [LeadActivityEvents.appointment_cancelled]:
    'pages.lead.activities.appointmentCancelled',
  [LeadActivityEvents.appointment_declined]:
    'pages.lead.activities.appointmentDeclined',
  [LeadActivityEvents.appointment_assignee_changed]:
    'pages.lead.activities.appointmentAssigneeChanged',
  [LeadActivityEvents.appointment_branch_service_changed]:
    'pages.lead.activities.appointmentBranchServiceChanged',
  [LeadActivityEvents.appointment_attendance_status_changed]:
    'pages.lead.activities.appointmentAttendanceStatusChanged',
  [LeadActivityEvents.comment]: 'pages.lead.activities.commentAdded',
  [LeadActivityEvents.extra_field_changed]:
    'pages.lead.activities.fieldChanged',
  [LeadActivityEvents.product_group_changed]:
    'pages.lead.activities.productGroupChanged',
  [LeadActivityEvents.file_uploaded]: 'pages.lead.activities.fileUploaded',
  [LeadActivityEvents.file_deleted]: 'pages.lead.activities.fileDeleted',
  [LeadActivityEvents.customer_file_uploaded]: 'pages.lead.activities.customerFileUploaded',
  [LeadActivityEvents.customer_file_deleted]: 'pages.lead.activities.customerFileDeleted',
  [LeadActivityEvents.order_volume_changed]: 'pages.lead.activities.dealValueChanged',
  [LeadActivityEvents.carat_planning_added]: 'pages.lead.activities.caratPlanningAdded',
  [LeadActivityEvents.carat_planning_updated]: 'pages.lead.activities.caratPlanningUpdated',
  [LeadActivityEvents.carat_planning_reassigned]: 'pages.lead.activities.caratPlanningReassigned',
  [LeadActivityEvents.trickpark_session_added]:
    'pages.lead.activities.trickparkSessionAdded',
  [LeadActivityEvents.task_note]: 'pages.lead.activities.taskNote',
  [LeadActivityEvents.contact_added]: 'pages.lead.activities.contactAdded',
  [LeadActivityEvents.contact_removed]: 'pages.lead.activities.contactRemoved',
  [LeadActivityEvents.primary_contact_changed]: 'pages.lead.activities.primaryContactChanged',
};
